import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Section, Row, Column } from 'components/atoms/Page';
import { Image } from 'components/atoms';
import placeholderImage from 'assets/images/facilitypage/placeholder.png';
import './FacilityGallery.scss';
import { ImageGallery } from 'components/organisms';
import MapView from '../MapView/MapView';

const MODALS = {
	MAP: 'map',
	GALLERY: 'gallery',
};

export function FacilityGallery({ facility }) {
	const [selectedIndex] = useState(0);
	const [openModal, setOpenModal] = useState(null);
	const [galleryStartIndex, setGalleryStartIndex] = useState(0);

	const images = facility?.images || [];
	const staticMapUrl = facility?.staticMapUrl;
	const hasFacilityImages = images.length > 0;

	const mainImage = hasFacilityImages
		? images[selectedIndex]
		: placeholderImage;

	// Thumbnails keep original index
	const imageThumbnails = useMemo(() => {
		if (!hasFacilityImages) return [];
		return images
			.map((src, index) => ({ src, index }))
			.filter(({ index }) => index !== selectedIndex);
	}, [images, selectedIndex, hasFacilityImages]);

	const MAX_VISIBLE_THUMBS = 3;
	const visibleImages = imageThumbnails.slice(0, MAX_VISIBLE_THUMBS);
	const remainingCount = Math.max(
		0,
		imageThumbnails.length - MAX_VISIBLE_THUMBS,
	);

	const thumbCols = (staticMapUrl ? 1 : 0) + visibleImages.length;

	const openGallery = (index) => {
		setGalleryStartIndex(index);
		setOpenModal(MODALS.GALLERY);
	};

	return (
		<Section>
			<Container>
				<Row>
					<Column>
						<div className="facility-gallery">
							{/* MAIN IMAGE */}
							<div className="facility-gallery__main">
								<button
									type="button"
									className="facility-gallery__main-button"
									aria-label={`Open gallery for ${facility.name}`}
									onClick={() => openGallery(selectedIndex)}
								>
									<Image
										src={
											hasFacilityImages
												? `https://images.selfstorage.com/large-compress/${mainImage}`
												: placeholderImage
										}
										alt={`Photo of ${facility.name}`}
										lazyLoad={false}
										fetchPriority="high"
										className="facility-gallery__main-image"
									/>
								</button>
							</div>

							{/* THUMBNAILS */}
							<div
								className="facility-gallery__thumbnails"
								style={{ '--icg-cols': thumbCols }}
							>
								{/* MAP THUMB */}
								{staticMapUrl && (
									<button
										type="button"
										className="facility-gallery__thumb"
										aria-label="Open facility map"
										onClick={() => setOpenModal(MODALS.MAP)}
									>
										<Image
											src={staticMapUrl}
											alt="Facility map"
											className="facility-gallery__thumb__image"
										/>
									</button>
								)}

								{/* IMAGE THUMBNAILS */}
								{visibleImages.map(({ src, index }, i) => {
									const isLastVisible =
										i === MAX_VISIBLE_THUMBS - 1 &&
										remainingCount > 0;

									return (
										<div
											key={`${src}-${index}`}
											className="facility-gallery__thumb"
										>
											<button
												type="button"
												className="facility-gallery__thumb-button"
												aria-label={`Open gallery starting at photo ${index + 1}`}
												onClick={() =>
													openGallery(index)
												}
											>
												<Image
													src={`https://images.selfstorage.com/large-compress/${src}`}
													alt={`Thumbnail ${index + 1}`}
													lazyLoad
													className="facility-gallery__thumb__image"
												/>
											</button>

											{/* OVERLAY FOR REMAINING */}
											{isLastVisible && (
												<button
													type="button"
													className="facility-gallery__overlay"
													aria-label={`View all ${images.length} photos`}
													onClick={() =>
														openGallery(index)
													}
												>
													<div className="facility-gallery__overlay__text">
														+{remainingCount} Photos
													</div>
												</button>
											)}
										</div>
									);
								})}
							</div>

							{/* MAP MODAL */}
							{openModal === MODALS.MAP && (
								<MapView
									isOpen
									onClose={() => setOpenModal(null)}
									facility={facility}
								/>
							)}

							{/* IMAGE GALLERY MODAL */}
							{openModal === MODALS.GALLERY && (
								<ImageGallery
									isOpen
									onClose={() => setOpenModal(null)}
									facility={facility}
									images={images}
									startIndex={galleryStartIndex}
								/>
							)}
						</div>
					</Column>
				</Row>
			</Container>
		</Section>
	);
}

FacilityGallery.propTypes = {
	facility: PropTypes.shape({
		name: PropTypes.string.isRequired,
		images: PropTypes.arrayOf(PropTypes.string),
		staticMapUrl: PropTypes.string,
	}),
};
export default FacilityGallery;
